const EMPTY_ARRAY: readonly [] = []

export function safeClone<T>(array: ReadonlyArray<T>): T[] {
  return [...(array ?? [])]
}

/**
 * Retorna um array vazio imutável com tipagem. Exemplo:
 *
 * ```ts
 * const myList = someList ?? emptyArrayOf<SomeListModel>();
 * ```
 */
export const emptyArrayOf = <T>(): ReadonlyArray<T> => EMPTY_ARRAY as ReadonlyArray<T>

/**
 * Particiona um array em dois grupos baseado em um predicado.
 *
 * Itera sobre o array fornecido e separa os elementos em dois arrays distintos:
 * o primeiro contém os elementos que satisfazem o predicado, e o segundo contém
 * os elementos que não satisfazem.
 *
 * @template T - O tipo dos elementos no array
 * @param array - O array a ser particionado
 * @param predicate - Função que determina o critério de separação dos itens
 * @returns Uma tupla onde o primeiro elemento é o array com itens que satisfazem
 *          o predicado e o segundo elemento é o array com itens que não satisfazem
 */
export function partition<T>(array: ReadonlyArray<T>, predicate: (item: T) => boolean): [T[], T[]] {
  const groupA: T[] = []
  const groupB: T[] = []

  for (const item of array) {
    if (predicate(item)) {
      groupA.push(item)
    } else {
      groupB.push(item)
    }
  }

  return [groupA, groupB]
}

/**
 * Remove itens duplicados por `id`, mantendo a primeira ocorrência e a ordem original.
 * Itens nulos ou indefinidos são ignorados.
 */
export const dedupById = <T extends { id: ID }>(items: ReadonlyArray<T | null | undefined>): T[] => {
  const seen = new Set<ID>()
  const out: T[] = []
  items.forEach((item) => {
    if (item && !seen.has(item.id)) {
      seen.add(item.id)
      out.push(item)
    }
  })
  return out
}

/**
 * Retorna os itens cujo `id` não está presente no conjunto informado, preservando a ordem original.
 */
export const excludeIds = <T extends { id: ID }>(items: ReadonlyArray<T>, ids: ReadonlySet<ID>): T[] =>
  items.filter((it) => !ids.has(it.id))
